import {
  Button,
  Flex,
  Heading,
  IconAddFolderLine,
  IconCollectionLine,
  IconFolderLine,
  IconSubaccountsLine,
  IconSyllabusLine,
  InlineList,
  Spinner,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { createContext, useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AcademicHierarchyService } from "../../services/AcademicHierarchyService";
import { CourseGroupService } from "../../services/CourseGroupService";
import { CourseGroupTypeService } from "../../services/CourseGroupTypeService";
import { CourseLevelService } from "../../services/CourseLevelService";
import { ProgramLevelService } from "../../services/ProgramLevelService";
import { ProgramTypeService } from "../../services/ProgramTypeService";
import { SubjectService } from "../../services/SubjectService";
import { UserDetailContext } from "../../utils/Context";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import SubLevel from "./Sublevel";

const ROOT = "root";

const ProgramContext = createContext();
const SubjectContext = createContext();
const CourseLevelsContext = createContext();
const CourseGroupsContext = createContext();
const CourseGroupTypesContext = createContext();

const OrganizationalStructure = () => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [hierarchy, setHierarchy] = useState(null);
  const [parentSpinner, setParentSpinner] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [courseLevels, setCourseLevels] = useState([]);
  const [courseGroupList, setCourseGroupList] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [programLevels, setProgramLevels] = useState([]);

  useEffect(() => {
    const promises = [
      AcademicHierarchyService.getLevels(ROOT),
      SubjectService.getSubjects({ allSubjects: true }),
      ProgramTypeService.getProgramTypes(),
      ProgramLevelService.getProgramLevels(),
      CourseLevelService.getCourseLevels(),
      CourseGroupService.getCourseGroups(),
      CourseGroupTypeService.getGroupTypes(),
    ];

    Promise.all(promises)
      .then(
        ([
          levels,
          subs,
          programTypes,
          programLevels,
          courseLevels,
          groups,
          groupTypes,
        ]) => {
          setHierarchy(levels);
          setSubjects(
            subs.map((subject) => ({
              id: subject.id,
              title: subject.title,
              name: subject.name,
              label: subject.name,
            }))
          );
          let types = programTypes.map((programType) => ({
            id: programType.id,
            label: programType.name,
          }));
          types = sort(types, (type) => type.label);
          setProgramTypes(types);
          let progLevels = programLevels.map((level) => ({
            id: level.id,
            label: level.name,
          }));
          progLevels = sort(progLevels, (level) => level.label);
          setProgramLevels(progLevels);
          setCourseLevels(
            courseLevels.map((level) => ({
              id: level.id,
              courseLevel: level.courseLevel,
              label: `${t("programs.courseLevel")} ${level.courseLevel} (${
                level.range.start
              } - ${level.range.end})`,
            }))
          );
          groups = sort(groups, (group) => group.name);
          setCourseGroupList(groups);
          setGroupTypes(
            groupTypes.map((type) => ({
              id: type.id,
              label: type.name,
              mode: "view",
            }))
          );
        }
      )
      .catch(() => showNotification(t("notifications.errorOccurred"), ERROR));
  }, [showNotification]);

  const handleAddLevel = async () => {
    setParentSpinner(true);
    try {
      const data = await AcademicHierarchyService.createLevel(
        "",
        t("academicHierarchy.newLevel")
      );
      data.ddl_id = data.id;

      setParentSpinner(false);
      setHierarchy((prevHierarchy) => [...prevHierarchy, data]);
      showNotification(
        t("notifications.general.addSuccess", {
          obj: t("academicHierarchy.hierarchyLevel"),
        })
      );
    } catch {
      setParentSpinner(false);
      showNotification(
        t("notifications.general.addError", {
          obj: t("academicHierarchy.hierarchyLevel"),
        }),
        ERROR
      );
    }
  };

  return (
    <View as="div">
      <Heading as="h2" level="h3" border="bottom" margin="0 0 small 0">
        {t("setup.academicHierarchy")}
      </Heading>
      <Flex justifyItems="space-between" vAlign="middle" gap="small">
        <Flex.Item>
          <InlineList size="small" variant="unstyled">
            <InlineList.Item>
              {t("academicHierarchy.legend.iconLegend")}:
            </InlineList.Item>
            <InlineList.Item>
              <IconFolderLine /> {t("academicHierarchy.legend.level")}
            </InlineList.Item>
            <InlineList.Item>
              <IconSubaccountsLine /> {t("academicHierarchy.legend.department")}
            </InlineList.Item>
            <InlineList.Item>
              <IconSyllabusLine /> {t("academicHierarchy.legend.program")}
            </InlineList.Item>
            <InlineList.Item>
              <IconCollectionLine /> {t("academicHierarchy.legend.courseGroup")}
            </InlineList.Item>
          </InlineList>
        </Flex.Item>
        {userDetails.isAC() && (
          <Flex.Item>
            <Button
              renderIcon={
                parentSpinner ? (
                  <Spinner
                    size="x-small"
                    renderTitle={t("global.pleaseWait")}
                  />
                ) : (
                  IconAddFolderLine
                )
              }
              color="primary"
              onClick={handleAddLevel}
              disabled={parentSpinner}
            >
              {t("academicHierarchy.addHierarchyLevel")}
            </Button>
          </Flex.Item>
        )}
      </Flex>
      {hierarchy ? (
        hierarchy.length > 0 ? (
          sort(hierarchy, (item) => item.name).map((level) => (
            <div key={level.ddl_id} style={{ marginTop: 10 }}>
              <SubjectContext.Provider value={{ subjects, setSubjects }}>
                <ProgramContext.Provider
                  value={{ programTypes, programLevels }}
                >
                  <CourseLevelsContext.Provider value={courseLevels}>
                    <CourseGroupsContext.Provider
                      value={[courseGroupList, setCourseGroupList]}
                    >
                      <CourseGroupTypesContext.Provider
                        value={[groupTypes, setGroupTypes]}
                      >
                        <SubLevel
                          key={level.ddl_id}
                          parent={null}
                          setParent={null}
                          level={level}
                          sub={false}
                          hierarchy={hierarchy}
                        />
                      </CourseGroupTypesContext.Provider>
                    </CourseGroupsContext.Provider>
                  </CourseLevelsContext.Provider>
                </ProgramContext.Provider>
              </SubjectContext.Provider>
            </div>
          ))
        ) : (
          <div>{t("academicHierarchy.nothingToShow")}</div>
        )
      ) : (
        <Skeleton
          count={3}
          style={{ height: 64, borderRadius: 5, marginTop: 10 }}
        />
      )}
    </View>
  );
};

export {
  CourseGroupsContext,
  CourseGroupTypesContext,
  CourseLevelsContext,
  OrganizationalStructure,
  ProgramContext,
  SubjectContext,
};
